import type { AxisSelection, ObjectTag, ObjectTagTuple } from '@atlas/types'
import { DEFAULT_TUPLE_ID } from '@atlas/types'

/** Any row that can carry a tuple ordinal — the minimum shape tuple-grouping needs. */
interface TupleTaggedRow {
	tupleId?: number
}

/**
 * Bucket rows by `tupleId` (untagged rows collapse into `DEFAULT_TUPLE_ID`, so a
 * list produced before tuples existed yields exactly one bucket), ascending by
 * ordinal. Returns `[]` for an empty input — callers that need "untagged is one
 * unconstrained assertion" instead of "no assertions" (the resolution engine's
 * coverage/browse rules) special-case the empty list themselves; this primitive
 * makes no assumption about what empty should mean, since that is caller policy,
 * not a grouping concern.
 *
 * Pure and dependency-free, generic over the row shape: `groupTagsByTuple` below
 * and the classification resolver's own tuple-splitting both bucket the same way
 * over different row types (`ObjectTag` with labels, vs the resolver's
 * label-less `TagRow`) — this is the one place that logic lives.
 */
export const bucketByTuple = <T extends TupleTaggedRow>(rows: T[]): [tupleId: number, rows: T[]][] => {
	const byTuple = new Map<number, T[]>()
	for (const row of rows) {
		const id = row.tupleId ?? DEFAULT_TUPLE_ID
		const group = byTuple.get(id)
		if (group) group.push(row)
		else byTuple.set(id, [row])
	}
	return [...byTuple.entries()].sort(([a], [b]) => a - b)
}

/**
 * Group an object's flat tag list into its tuples, ascending by ordinal.
 *
 * Tags with no `tupleId` collapse into the default tuple, so a list produced
 * before tuples existed yields exactly one group — the union and the grouping
 * agree for a single-tuple object, which is what keeps the change invisible to
 * callers that have not opted in. An object with no tags yields no groups (`[]`)
 * — this is the read-side hydration shape, where "nothing to render" is correct;
 * contrast the resolution engine's own grouping, which needs an untagged object
 * to still evaluate as one unconstrained assertion.
 *
 * Pure and dependency-free: both the classification service (composing the batch
 * tags read) and the frontend (rendering assertions as separate groups) need the
 * same grouping, so it lives here rather than in either one.
 */
export const groupTagsByTuple = (tags: ObjectTag[]): ObjectTagTuple[] =>
	bucketByTuple(tags).map(([tupleId, tupleTags]) => ({ tupleId, tags: tupleTags }))

/** The minimum shape hierarchical ordering needs: an identity, its parent, and a label to sort siblings by. */
interface HierarchicalValue {
	valueId: string | number
	parentId: string | number | null
	label: string
}

/**
 * Order axis values into reading order: every value immediately follows its
 * parent, and siblings are alphabetical by label. A flat (non-hierarchical) axis
 * is therefore just sorted alphabetically, since every value is a root.
 *
 * Without this, a value's rendered indentation is meaningless — a dog breed can
 * sort between two unrelated species, so the list looks scrambled even though
 * each row's depth is correct. Depth alone conveys hierarchy only when a child
 * is contiguous with its parent.
 *
 * Values whose parent is absent from the input (a child whose parent is inactive
 * or filtered out) are treated as roots, so they are never silently dropped.
 * Pure and dependency-free: the bootstrap read and the facet read both need the
 * same order over different row shapes, so it lives here rather than in either.
 */
export const sortValuesByHierarchy = <T extends HierarchicalValue>(values: T[]): T[] => {
	const byParent = new Map<string, T[]>()
	const present = new Set(values.map((v) => String(v.valueId)))
	const rootKey = ''

	for (const value of values) {
		const parent = value.parentId === null ? null : String(value.parentId)
		const key = parent !== null && present.has(parent) ? parent : rootKey
		const siblings = byParent.get(key)
		if (siblings) siblings.push(value)
		else byParent.set(key, [value])
	}

	const byLabel = (a: T, b: T) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' })
	const ordered: T[] = []
	// Guards two ways a malformed input would otherwise recurse forever or emit a
	// subtree twice: a value whose id equals the root key (so it buckets under
	// itself), and two rows sharing an id (so each descends into the same
	// children). Neither is reachable from a `bigserial` primary key, but this is
	// shared, generic code — a caller's bad join must not hang the request.
	const visited = new Set<string>()
	const visit = (key: string) => {
		if (visited.has(key)) return
		visited.add(key)
		const siblings = byParent.get(key)
		if (!siblings) return
		for (const value of [...siblings].sort(byLabel)) {
			ordered.push(value)
			visit(String(value.valueId))
		}
	}
	visit(rootKey)

	// A parent cycle would leave rows unvisited; append them rather than drop them.
	if (ordered.length < values.length) {
		const emitted = new Set(ordered.map((v) => String(v.valueId)))
		ordered.push(...values.filter((v) => !emitted.has(String(v.valueId))))
	}
	return ordered
}

/**
 * Whether an axis selection actually NARROWS anything.
 *
 * The guard every listing driven by a selection needs, because a search or facet on an empty
 * selection matches EVERY classified object — so an unclassified subject silently listed the
 * whole library as its context. A work order with no tuple showed 59 files, raw uuids, under
 * "reference deliverables"; the honest answer was none, and the gap is what a reader acts on.
 *
 * An axis present with an EMPTY value list narrows nothing either: `{ brand: [] }` is exactly as
 * unselective as `{}`, and a bare `Object.keys(...).length > 0` check mistakes it for a real
 * selection. That is the case this exists to catch.
 *
 * "No selection" is NOT the same statement as "no matches". A caller that finds this false should
 * report that nothing was asked, rather than run the read and present everything as relevant.
 */
export const hasAxisSelection = (selection: AxisSelection | undefined): selection is AxisSelection =>
	activeAxesOf(selection).length > 0

/**
 * The axes in a selection that actually carry values, in insertion order.
 *
 * One definition of "an axis is being filtered on", so the emptiness CHECK and the axis LIST
 * cannot disagree — a caller that finds `hasAxisSelection` true then iterates exactly these
 * axes. Both were hand-written in five places before this, each re-deriving
 * `Object.keys(f).filter((a) => (f[a] ?? []).length > 0)`; one of those copies used a bare
 * `Object.keys(...).length > 0` and so mistook `{ brand: [] }` for a real selection.
 *
 * An axis present with no values is dropped: it narrows nothing, and keeping it would make a
 * matcher demand a match on an axis the caller never actually constrained.
 */
export const activeAxesOf = (selection: AxisSelection | undefined): string[] =>
	selection === undefined ? [] : Object.keys(selection).filter((axisCode) => (selection[axisCode] ?? []).length > 0)

/**
 * The resolution contexts to ask rule-service for, given a file's classification.
 *
 * One context PER ASSERTION. A file's tuples are independent claims about it, so
 * each is resolved on its own and the results unioned. Flattening them into one
 * context invents pairings the file never asserted: a file that is "a US article"
 * and, separately, "a Spanish video" would ask for rules covering
 * `{geography: [US, ES], asset_type: [article, video]}` — reaching a rule scoped to
 * *Spanish articles*, which it never claims to be.
 *
 * A file with no tuples still yields exactly ONE context, built from the flat tag
 * list. That covers both an untagged file and a caller still on the pre-tuple
 * payload, and an empty context is meaningful rather than a no-op: it resolves the
 * rules that apply universally.
 *
 * Pure and dependency-free, so it lives here rather than in one consumer: file-service
 * resolves a file's rules with it, and rule-validation-worker resolves each of a run's
 * subjects with the same mapping.
 */
export function applicableRulesContexts(tags: ObjectTag[], tuples: ObjectTagTuple[]): AxisSelection[] {
	const toContext = (rows: ObjectTag[]): AxisSelection => {
		const context: AxisSelection = {}
		for (const t of rows) context[t.axisCode] = [...(context[t.axisCode] ?? []), t.valueCode]
		return context
	}
	return tuples.length > 0 ? tuples.map((t) => toContext(t.tags)) : [toContext(tags)]
}

/** The minimum shape a hierarchical axis value needs for {@link brandPathOf}. */
interface HierarchyValue {
	valueId: string
	parentId: string | null
	code: string
}

/**
 * A brand-axis value and its ancestors, most specific first: `[product, …family, brand]`, or
 * `[brand]` for a root.
 *
 * Brand and product are two levels of ONE axis, so an object tagged for a product carries no
 * separate brand tag: the brand is only reachable by walking up. A consumer that needs "the
 * brand" must take the root, and one that narrows by applicability must accept the whole path
 * — content tagged for the brand applies to every product under it.
 *
 * A code the registry does not know is its own path, so an unregistered value still scopes
 * the read it was going to scope.
 *
 * An ancestor missing mid-walk (a `parentId` that resolves to nothing in `values`) stops the
 * walk there rather than throwing: this runs isomorphically, over the client's cached
 * registry as much as a server request, and a taxonomy gap is not this shared primitive's to
 * report. `ClassificationService.retireValue` is what actually prevents the gap — it refuses
 * to retire a value with active children — so an ancestor should only ever be missing here
 * because it was never active in the first place (an unregistered code, already handled
 * above), not because a live child outlived its retired parent.
 */
export function brandPathOf(values: HierarchyValue[], code: string | null | undefined): string[] {
	if (!code) return []
	const byId = new Map<string, HierarchyValue>(values.map((v) => [v.valueId, v]))
	let value: HierarchyValue | undefined = values.find((v) => v.code === code)
	if (!value) return [code]
	const path: string[] = []
	while (value && !path.includes(value.code)) {
		path.push(value.code)
		const parentId: string | null = value.parentId
		value = parentId ? byId.get(parentId) : undefined
	}
	return path
}

/**
 * The deepest {@link brandPathOf} among an object's `brand` codes.
 *
 * An object tagged by a person carries one brand code, the product when one was chosen. One
 * written by an integration (a Workfront order) can carry the brand AND its product side by
 * side, in no promised order, so reading only the first code drops the product whenever the
 * brand happens to come first. The longest path is the most specific value, and it already
 * holds the others as its ancestors. Ties keep the first code.
 */
export function mostSpecificBrandPathOf(
	values: HierarchyValue[],
	codes: string | readonly string[] | null | undefined,
): string[] {
	const list = typeof codes === 'string' ? [codes] : (codes ?? [])
	let deepest: string[] = []
	for (const code of list) {
		const path = brandPathOf(values, code)
		if (path.length > deepest.length) deepest = path
	}
	return deepest
}

/** The brand (the path's root) and the product (the tagged value, when it sits below one). */
export function brandAndProductOf(path: string[]): { brandCode: string | null; productCode: string | null } {
	return { brandCode: path.at(-1) ?? null, productCode: path.length > 1 ? path[0] : null }
}

/**
 * An external code as a reverse lookup compares it: NFKC, whitespace collapsed and
 * trimmed, lowercase. The database computes the same on `value_mapping.external_code_norm`,
 * so a code normalised here matches the stored one.
 */
export function normalizeExternalCode(code: string): string {
	return code.normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase()
}
